import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, buttonClass } from '@/components/ui/Button';
import { useAppSelector, selectUser } from '@/app/store';
import { useAuth } from '@/lib/auth/useAuth';
import { authService } from '@/lib/api/services/auth.service';

/**
 * A gentle, dismissible nudge toward enabling MFA — never a requirement.
 * Shown once per app load (not re-triggered on every navigation) when the
 * signed-in user has no MFA and hasn't snoozed the suggestion recently.
 *
 * "Remind me later" persists a snooze server-side; the close (×) only hides it
 * for this load, so it can still reappear next time — closing isn't a "no".
 */
export function MfaSuggestionToast() {
  const user = useAppSelector(selectUser);
  const { refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [snoozing, setSnoozing] = useState(false);

  const snoozedUntil = user?.mfaPromptSnoozedUntil ? new Date(user.mfaPromptSnoozedUntil) : null;
  const shouldShow = !!user && !user.mfaEnabled && (!snoozedUntil || snoozedUntil.getTime() < Date.now());

  if (!shouldShow || dismissed) return null;

  const remindLater = async () => {
    setSnoozing(true);
    try {
      await authService.mfaSnooze();
      await refreshUser();
    } finally {
      setSnoozing(false);
      setDismissed(true);
    }
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Add two-factor authentication"
      className="fixed bottom-5 right-5 z-[60] w-[340px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-ink-900/[0.08] bg-white shadow-popover"
    >
      <div className="h-1 bg-gradient-to-r from-primary-600 via-primary-500 to-brass-500" aria-hidden="true" />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="font-display text-[13.5px] font-bold leading-snug text-ink-900">Add two-factor authentication?</p>
              <button
                type="button"
                onClick={() => setDismissed(true)}
                aria-label="Dismiss for now"
                className="-mr-1 -mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ink-400 transition-colors hover:bg-ink-900/[0.05] hover:text-ink-700"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-3.5 w-3.5" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="mt-1 text-[12px] leading-relaxed text-ink-600">
              Protect your account with a code from an authenticator app, in addition to your password.
            </p>
            <p className="mt-1.5 text-[11px] font-medium text-ink-400">Optional — you can turn this on any time in Account security.</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2 border-t border-ink-900/[0.06] pt-3">
          <Button size="sm" variant="ghost" onClick={() => void remindLater()} loading={snoozing}>
            Remind me later
          </Button>
          <Link to="/account/security" onClick={() => setDismissed(true)} className={buttonClass({ size: 'sm' })}>
            Enable
          </Link>
        </div>
      </div>
    </div>
  );
}
